import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Storefront, Copy, ShareNetwork, DownloadSimple, Crown,
  Phone, MapPin, IdentificationBadge, SignOut, ArrowSquareOut,
  CheckCircle, Sparkle, PencilSimple, X, Envelope
} from "@phosphor-icons/react";

export default function ShopProfile() {
  const { user, setUser, logout } = useAuth();
  const nav = useNavigate();
  const [store, setStore] = useState(null);
  const [me, setMe] = useState(user);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const qrRef = useRef(null);

  useEffect(() => {
    // Re-fetch fresh user + store so the cached localStorage user can't go stale
    api.get("/auth/me").then((r) => {
      setMe(r.data);
      setUser(r.data);
      localStorage.setItem("qd_user", JSON.stringify(r.data));
      setStore(r.data.store || null);
    }).catch(() => setStore(false));
  // eslint-disable-next-line
  }, []);

  if (store === null) return <div className="p-6">Loading…</div>;
  if (store === false) return <div className="p-6 text-center text-gray-500">No store found.</div>;

  const origin = window.location.origin;
  const storeUrl = `${origin}/${store.slug}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const shareUrl = async () => {
    const shareData = {
      title: store.name,
      text: `Order fresh groceries from ${store.name} on QuickDukaan!`,
      url: storeUrl,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); }
      catch { /* user cancelled */ }
    } else {
      copyUrl();
    }
  };

  const downloadQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return toast.error("QR not ready");
    // Render the QR into a larger canvas with branding
    const exp = document.createElement("canvas");
    exp.width = 800;
    exp.height = 1000;
    const ctx = exp.getContext("2d");
    // BG
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, 800, 1000);
    // Title
    ctx.fillStyle = "#0A0A0A";
    ctx.font = "bold 36px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Scan to shop at", 400, 80);
    ctx.font = "bold 56px sans-serif";
    ctx.fillText(store.name, 400, 150);
    // QR
    ctx.drawImage(canvas, 100, 200, 600, 600);
    // Footer
    ctx.fillStyle = "#15803D";
    ctx.font = "bold 28px sans-serif";
    ctx.fillText(storeUrl, 400, 870);
    ctx.fillStyle = "#777";
    ctx.font = "20px sans-serif";
    ctx.fillText("Powered by QuickDukaan · Delivered in minutes", 400, 920);

    const dataUrl = exp.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${store.slug}-quickdukaan-qr.png`;
    a.click();
    toast.success("QR downloaded");
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-4xl fade-up">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight">My Profile</h1>
        <p className="text-gray-600 text-sm">Manage your store identity and share your URL</p>
      </div>

      {/* Store URL & QR Card */}
      <div className="qd-card overflow-hidden">
        <div className="bg-gradient-to-br from-[#15803D] to-[#166534] text-white p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-widest font-bold text-[#F8CB46] mb-1">Your store URL</div>
              <div className="font-heading font-black text-xl sm:text-2xl break-all" data-testid="profile-store-url">
                {origin.replace(/^https?:\/\//, "")}/{store.slug}
              </div>
              <a href={storeUrl} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[#F8CB46] hover:text-white transition"
                data-testid="profile-open-store">
                Open storefront <ArrowSquareOut size={12} weight="bold" />
              </a>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
              <Storefront weight="fill" size={28} className="text-[#F8CB46]" />
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <button onClick={copyUrl}
              data-testid="profile-copy-btn"
              className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl p-3 transition active:scale-95">
              {copied ? <CheckCircle weight="fill" size={20} className="text-[#F8CB46]" /> : <Copy weight="bold" size={20} />}
              <span className="text-[11px] font-bold">{copied ? "Copied" : "Copy link"}</span>
            </button>
            <button onClick={shareUrl}
              data-testid="profile-share-btn"
              className="flex flex-col items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur rounded-xl p-3 transition active:scale-95">
              <ShareNetwork weight="bold" size={20} />
              <span className="text-[11px] font-bold">Share</span>
            </button>
            <button onClick={downloadQR}
              data-testid="profile-download-qr"
              className="flex flex-col items-center gap-1 bg-[#F8CB46] text-black hover:brightness-95 rounded-xl p-3 transition active:scale-95">
              <DownloadSimple weight="bold" size={20} />
              <span className="text-[11px] font-bold">Download QR</span>
            </button>
          </div>
        </div>

        {/* QR display */}
        <div className="bg-white p-6 flex flex-col sm:flex-row items-center gap-5">
          <div ref={qrRef} className="p-3 rounded-2xl bg-white border-4 border-[#F8CB46]" data-testid="profile-qr-canvas">
            <QRCodeCanvas
              value={storeUrl}
              size={180}
              level="H"
              fgColor="#0A0A0A"
              bgColor="#FFFFFF"
              imageSettings={undefined}
            />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <div className="inline-flex items-center gap-1 bg-[#F8CB46]/15 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold mb-2">
              <Sparkle weight="fill" size={10} className="text-[#15803D]" /> Print this QR
            </div>
            <h3 className="font-heading font-black text-xl">Place at your counter</h3>
            <p className="text-sm text-gray-600 mt-1">
              Customers can scan this QR to instantly open your storefront — no app install needed.
              Print it, share on WhatsApp, or send via SMS.
            </p>
          </div>
        </div>
      </div>

      {/* Owner & Store info */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="qd-card p-5 relative">
          <button onClick={() => setEditing(true)}
            data-testid="profile-edit-owner-btn"
            className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-xs font-bold inline-flex items-center gap-1 transition active:scale-95">
            <PencilSimple size={12} weight="bold" /> Edit
          </button>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Owner</div>
          <h3 className="font-heading font-black text-lg mt-1" data-testid="profile-owner-name">
            {me?.name || "Shopkeeper"}
          </h3>
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Phone size={14} weight="bold" />
              <span className="font-mono">+91 {me?.mobile}</span>
            </div>
            {me?.email && (
              <div className="flex items-center gap-2 text-gray-600">
                <Envelope size={14} weight="bold" />
                <span className="break-all">{me.email}</span>
              </div>
            )}
          </div>
        </div>

        <div className="qd-card p-5 relative">
          <button onClick={() => nav("/admin/settings")}
            data-testid="profile-edit-store-btn"
            className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-xs font-bold inline-flex items-center gap-1 transition active:scale-95">
            <PencilSimple size={12} weight="bold" /> Edit
          </button>
          <div className="text-xs uppercase tracking-widest font-bold text-gray-500">Store</div>
          <h3 className="font-heading font-black text-lg mt-1">{store.name}</h3>
          <div className="mt-3 space-y-2 text-sm text-gray-600">
            <div className="flex items-start gap-2">
              <MapPin size={14} weight="bold" className="mt-0.5 flex-shrink-0" />
              <span>{store.address}, {store.city} - {store.pincode}</span>
            </div>
            {store.gstin && (
              <div className="flex items-center gap-2">
                <IdentificationBadge size={14} weight="bold" />
                <span className="font-mono">{store.gstin}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscription pill */}
      <div className="qd-card p-5 flex flex-wrap items-center gap-4 bg-[#F8CB46]/10 border-[#F8CB46]/40">
        <div className="w-12 h-12 rounded-xl bg-[#F8CB46] flex items-center justify-center">
          <Crown weight="fill" size={22} className="text-black" />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-xs uppercase tracking-widest font-bold text-gray-600">Current plan</div>
          <div className="font-heading font-black text-xl capitalize">{store.subscription_plan}</div>
          <div className="text-xs text-gray-500">
            Status: <span className="font-semibold capitalize">{store.subscription_status}</span>
          </div>
        </div>
        <button onClick={() => nav("/admin/subscription")}
          className="qd-btn-primary text-sm" data-testid="profile-manage-sub">
          Manage plan →
        </button>
      </div>

      <button onClick={() => { logout(); nav("/seller/login"); }}
        className="qd-card p-4 w-full flex items-center justify-center gap-2 text-red-600 font-bold hover:bg-red-50 transition"
        data-testid="profile-logout">
        <SignOut size={18} weight="bold" /> Logout
      </button>

      {editing && (
        <EditOwnerModal
          initial={me}
          onClose={() => setEditing(false)}
          onSaved={(updated) => {
            setMe(updated);
            setUser(updated);
            localStorage.setItem("qd_user", JSON.stringify(updated));
            setEditing(false);
          }}
        />
      )}
    </div>
  );
}

function EditOwnerModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", { name: name.trim(), email: email.trim() });
      toast.success("Profile updated");
      onSaved(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 fade-up" onClick={onClose}>
      <div className="bg-white w-full max-w-md max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-owner-modal">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-heading font-black text-xl">Edit profile</h2>
            <p className="text-xs text-gray-500">Your name and contact info</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200" data-testid="edit-owner-close">
            <X size={16} weight="bold" />
          </button>
        </div>

        <form onSubmit={save} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Name *</label>
            <input className="qd-input mt-2" value={name}
              onChange={(e) => setName(e.target.value)} data-testid="edit-owner-name" autoFocus />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-gray-500">Email</label>
            <input type="email" className="qd-input mt-2" value={email}
              onChange={(e) => setEmail(e.target.value)} data-testid="edit-owner-email"
              placeholder="you@example.com" />
          </div>
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
            Mobile number can't be changed (used for login).
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-3 rounded-full bg-gray-100 font-bold hover:bg-gray-200"
              data-testid="edit-owner-cancel">
              Cancel
            </button>
            <button type="submit" disabled={saving}
              className="qd-btn-green flex-1 disabled:opacity-60"
              data-testid="edit-owner-save">
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
